import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchScsDescriptionOfStudies } from "../store/slices/scsDataSlice";

const EMPTY = [];

/**
 * The grouping names the user defined in Q2A ("Define your study groupings and
 * add the corresponding studies") of the Description of Studies page. They are
 * saved as the keys of `groupings.qGroupingDefinitions` — { name: [studies] } —
 * so the keys are the names, in the order they were entered. Blank names are
 * never saved, but trim/de-dupe defensively so a stray entry can't produce an
 * unlabelled or duplicated grouping downstream.
 */
export const scsGroupingNamesFrom = data => {
  const definitions = data?.groupings?.qGroupingDefinitions;
  if (!definitions || typeof definitions !== "object") return EMPTY;
  const names = [
    ...new Set(
      Object.keys(definitions)
        .map(name => (name || "").trim())
        .filter(Boolean)
    ),
  ];
  return names.length ? names : EMPTY;
};

/**
 * Read the Description of Studies answers out of the `scsData` slice, fetching
 * them once per summary if they aren't there yet (a page can be landed on
 * directly, without passing through the Description of Studies page). The
 * thunk's condition guard dedupes, so re-mounts won't re-request.
 *
 * `summaryId` defaults to the `:studyId` route param every SCS page already
 * uses.
 */
export const useScsDescriptionOfStudies = summaryId => {
  const dispatch = useDispatch();
  const { studyId } = useParams();
  const id = summaryId ?? studyId;
  const bucket = useSelector(state => state.scsData.descriptionOfStudies);

  useEffect(() => {
    if (id) dispatch(fetchScsDescriptionOfStudies(id));
  }, [dispatch, id]);

  const groupingNames = useMemo(
    () => scsGroupingNamesFrom(bucket.data),
    [bucket.data]
  );

  return {
    data: bucket.data,
    groupingNames,
    isLoadingData: bucket.loading,
    dataError: bucket.error,
    fetched: bucket.fetched,
  };
};
